import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLang } from '../hooks';
import { getPhotoAlbums, getPhotoAlbum, getPhotos, getPhotoPublicUrl, type PhotoAlbum, type Photo } from '../services/api/photos';
import './PhotosPage.css';

function GalleryGrid({ album, photos }: { album: PhotoAlbum; photos: Photo[] }) {
  const locale = useLang().lang;
  const [lightbox, setLightbox] = useState<number | null>(null);
  const title = locale === 'th' && album.title_th ? album.title_th : album.title_en;

  return (
    <div className="photos-page page">
      <h1 className="photos-gallery__title">{title}</h1>
      <div className="photos-gallery__grid">
        {photos.map((p, i) => (
          <button key={p.id} type="button" onClick={() => setLightbox(i)} className="photos-gallery__thumb">
            <img
              src={getPhotoPublicUrl(p.storage_path)}
              alt={locale === 'th' && p.caption_th ? p.caption_th : p.caption_en || ''}
            />
          </button>
        ))}
      </div>
      {lightbox !== null && (
        <div className="photos-lightbox" onClick={() => setLightbox(null)} role="dialog" aria-modal="true">
          <img
            src={getPhotoPublicUrl(photos[lightbox].storage_path)}
            alt=""
            className="photos-lightbox__img"
            onClick={(e) => e.stopPropagation()}
          />
          <button type="button" onClick={() => setLightbox(null)} className="photos-lightbox__close" aria-label="Close">×</button>
        </div>
      )}
    </div>
  );
}

export default function PhotosPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id?: string }>();
  const [albums, setAlbums] = useState<PhotoAlbum[]>([]);
  const [album, setAlbum] = useState<PhotoAlbum | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPhotoAlbums().then(setAlbums).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getPhotoAlbum(id)
      .then((a) => {
        setAlbum(a);
        return getPhotos(a.id);
      })
      .then(setPhotos)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  if (loading && !album) return <div className="page-loading">{t('common.loading')}</div>;

  if (id && album) return <GalleryGrid album={album} photos={photos} />;

  return (
    <div className="photos-page page">
      <h1 className="photos-page__title">{t('nav.photos')}</h1>
      <div className="photos-page__grid">
        {albums.map((a) => (
          <Link key={a.id} to={`/photos/${a.id}`} className="photos-page__album">
            {a.title_en}
          </Link>
        ))}
      </div>
      {albums.length === 0 && <p className="page-empty">No galleries yet.</p>}
    </div>
  );
}
